import { type FC, useMemo } from 'react';

import { MarkdownText } from '@vakantiesnl/components/src/__LEGACY__/atoms/MarkdownText/MarkdownText';
import { Icon } from '@vakantiesnl/components/src/atoms/Icon';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { getFormattedRoundedPrice } from '@vakantiesnl/services/src/util/mathUtils';
import { inject } from '@vakantiesnl/services/src/util/template';
import { Awards } from '@vakantiesnl/types';
import { DISCOUNT_TYPE } from '@vakantiesnl/types/src/flash-deals';
import { type FullAccommodationItem } from '@vakantiesnl/types/src/search';

import { useCustomStyles } from './USPs.style';
import { List } from '../List/List';

type UspProps = {
	accommodation: FullAccommodationItem;
	discountAmount?: number | null;
	discountAmountType?: DISCOUNT_TYPE | null;
	isAccoPage?: boolean;
	accoLastYearAward?: Awards.Award;
};

export const USPs: FC<UspProps> = ({
	accommodation,
	discountAmount,
	discountAmountType,
	isAccoPage,
	accoLastYearAward,
}) => {
	const accoRating = accommodation.rating;
	const bookableAccommodation = accommodation.isBookable;
	/** A acco rating is considered high when above or equal to 8 */
	const hasAccoHighRating = useMemo(() => {
		return accoRating >= 8 ? true : false;
	}, [accoRating]);

	const { classes } = useCustomStyles();
	const microCopies = useMicroCopyContext();

	const amountValue = useMemo(() => {
		if (discountAmount) {
			return discountAmountType === DISCOUNT_TYPE.PERCENTAGE
				? `${discountAmount}%`
				: getFormattedRoundedPrice(discountAmount);
		}
	}, [discountAmount, discountAmountType]);

	/* This conditions will only be applied in the AccommodationOverview */
	if (isAccoPage && !bookableAccommodation && !hasAccoHighRating) {
		return null;
	}

	return (
		<List className={classes.container} data-cy="usp-container">
			{hasAccoHighRating && (
				<List.Item icon={<Icon type="star" size="medium" className={classes.star} />}>
					<MarkdownText
						content={inject(microCopies['booking.usp.hotel.score'], {
							rating: `**${accoRating}**`, // Using Markdown syntax to make text bold
						}).join('')}
					/>
				</List.Item>
			)}
			{/* Show either the zoover deal or the lowest price usp */}
			{process.env.NEXT_PUBLIC_BRAND === 'ZVRNL' && amountValue ? (
				<List.Item icon={<Icon type={'bolt'} size="medium" className={classes.bolt} />} data-cy="zoover-deal">
					<MarkdownText
						content={inject(microCopies['booking.usp.zoover.deal.description'], {
							deal: `**${microCopies['booking.usp.zoover.deal']}**`, // Using Markdown syntax to make text bold
							percentage: `**${amountValue}**`, // Using Markdown syntax to make text bold
						}).join('')}
					/>
				</List.Item>
			) : (
				bookableAccommodation && (
					<List.Item
						icon={
							<Icon
								type={process.env.NEXT_PUBLIC_BRAND === 'ZVRNL' ? 'zoover' : 'checkmark'}
								size="medium"
								className={classes.brand}
							/>
						}
					>
						<MarkdownText
							content={inject(microCopies['booking.usp.guarantee'], {
								price: `**${microCopies['booking.usp.lowestPrice']}**`, // Using Markdown syntax to make text bold
							}).join('')}
						/>
					</List.Item>
				)
			)}

			{!isAccoPage && (
				<List.Item icon={<Icon type="checkmark" size="medium" className={classes.checkmark} />}>
					<MarkdownText
						content={inject(microCopies['booking.usp.duration'], {
							minutes: `**${microCopies['booking.usp.booking.within']}**`, // Using Markdown syntax to make text bold
						}).join('')}
					/>
				</List.Item>
			)}

			{/* We display the award message only when an acco has an award the previous year*/}
			{process.env.NEXT_PUBLIC_BRAND === 'ZVRNL' && accoLastYearAward && (
				<List.Item
					icon={
						<Icon
							type="award"
							size="medium"
							className={
								accoLastYearAward.awardType === Awards.AwardType.Gold
									? classes.goldAward
									: classes.silverAward
							}
						/>
					}
				>
					<MarkdownText
						content={inject(microCopies['accomodation.usps.award'], {
							awardType: `**${
								accoLastYearAward.awardType === Awards.AwardType.Gold
									? microCopies['acco.goldAward']
									: microCopies['acco.silverAward']
							}**`,
							brandAward: `**${microCopies['acco.zooverAward']}**`, // Using Markdown syntax to make text bold
						}).join('')}
					/>
				</List.Item>
			)}
		</List>
	);
};
